import React, { useEffect, useRef, useState } from 'react';
import { useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { WorldPayTransaction, createWorldPayTransaction, expireWorldPayTransaction } from '../../../API/GoMoto';
import { Spinner } from '../../../controls/Spinner/Spinner';
import css from './WorldPayCardInformation.module.scss';
import { useLocalization } from '../../../context/Localization/localization';
import { BillingInformation } from './WorldPayBillingInformation';
import { GMButton } from '../../../controls/Button';
import { GMDialog } from '../../../controls/Dialog';
import { DealerStatus } from '../../../API/Enums';

interface IProps {
    onSkip: (stripeSessionId?: string) => void;
    onReload: () => void;
    onError: () => void;
    billingInfo: BillingInformation;
}

export const WorldPayCardInformation: React.FC<IProps> = ({ onSkip, onReload, onError, billingInfo }) => {
    const appointment = useAppointmentStateUnsafe();
    const Localization = useLocalization();
    const {
        dealer: { key: dealerKey, status },
    } = useKioskState();
    const sessionCreated = useRef<boolean>(false);
    const [transaction, setTransaction] = useState<WorldPayTransaction | undefined>(undefined);
    const [showTransactionExpired, setShowTransactionExpired] = useState<boolean>(false);

    useEffect(() => {
        let timer: NodeJS.Timeout | undefined = undefined;

        if (transaction) {
            // WorldPay transactions expire after 10 minutes
            timer = setTimeout(() => {
                setShowTransactionExpired(true);
            }, 600000);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };

    }, [transaction]);

    useEffect(() => {
        if (appointment && !sessionCreated.current) {
            sessionCreated.current = true;

            createWorldPayTransaction(appointment.key, dealerKey, billingInfo.Street, billingInfo.ZipCode, billingInfo.City, billingInfo.State)
                .then((data) => {
                    if (data === null) {
                        onError();
                    }
                    else if (data.paymentStatus === 'paid') {
                        onSkip(data.transactionSetupId);
                    }
                    else if (data.paymentStatus === 'marked paid' || data.paymentStatus === 'no payment due') {
                        onReload();
                    }
                    else {
                        setTransaction(data);
                    }
                })
                .catch(() => onError());
        }
    }, [appointment, dealerKey, onSkip, onReload, onError, billingInfo.Street, billingInfo.ZipCode, billingInfo.City, billingInfo.State]);

    const onConfirmExpired = () => {
        setShowTransactionExpired(false);
        setTransaction(undefined);

        expireWorldPayTransaction(appointment?.key ?? '', dealerKey)
            .finally(() => onReload());
    };

    return (
        <div className={css.root}>
            <GMDialog
                ariaLabel={Localization.SessionExpired}
                open={showTransactionExpired}
                title={Localization.SessionExpired}
                actions={[
                    <GMButton
                        id={Localization.OK}
                        title={Localization.OK}
                        ariaLabel={Localization.OK}
                        onClick={onConfirmExpired}
                    >
                        {Localization.OK}
                    </GMButton>,
                ]}
            >
                <div>{Localization.YourPaymentSessionHasExpired}</div>
            </GMDialog>
            <div className={css.form}>
                {!transaction ?
                    <Spinner /> :
                    <iframe
                        className={css.frame}
                        height={'100%'}
                        width={'100%'}
                        src={`${status === DealerStatus.Test ?
                            (process.env.REACT_APP_WORLD_PAY_URL_TEST ?? process.env.REACT_APP_WORLD_PAY_URL) :
                            process.env.REACT_APP_WORLD_PAY_URL}?TransactionSetupID=${transaction.transactionSetupId}`}
                        title={'world-pay-payment'}
                    />
                }
            </div>
        </div>
    );
};
